import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  CarFront,
  Search,
  Filter,
  Fuel,
  Cog,
  Gauge,
  Trash2,
  Plus,
  BadgeDollarSign,
  Calendar,
  ImagePlus,
  X,
} from "lucide-react";
import { Link } from "react-router-dom";
import { api, apiErrorMessage } from "../lib/api";
import { currencyTRY, numberTR, dateTR, daysSince, today } from "../lib/format";
import { vehicleCost, vehicleProfit, MASRAF_KATEGORI } from "../lib/constants";
import { imgSrc, storageSrc } from "../lib/images";
import { useAuth } from "../lib/auth";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { Skeleton } from "../components/ui/skeleton";

function DurumBadge({ durum }) {
  const map = {
    Stokta: "bg-emerald-100 text-emerald-800",
    Satıldı: "bg-stone-200 text-stone-700",
    Rezerve: "bg-amber-100 text-amber-800",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold ${
        map[durum] || "bg-stone-100 text-stone-700"
      }`}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      {durum}
    </span>
  );
}

function VehicleCard({ v, onClick }) {
  const maliyet = vehicleCost(v);
  const stokGun = v.durum === "Stokta" ? daysSince(v.alisTarihi) : null;
  return (
    <button
      onClick={onClick}
      data-testid={`veh-card-${v.plaka.replace(/\s/g, "")}`}
      className="text-left rounded-2xl border border-stone-200 bg-white overflow-hidden hover:-translate-y-0.5 hover:shadow-lg hover:shadow-stone-200/50 transition-all"
    >
      <div className="aspect-[16/9] bg-stone-100 overflow-hidden relative">
        {v.gorsel ? (
          <img
            src={imgSrc(v.gorsel)}
            alt={`${v.marka} ${v.model}`}
            className="w-full h-full object-cover"
          />
        ) : (v.gorseller && v.gorseller[0]) ? (
          <img
            src={storageSrc(v.gorseller[0])}
            alt={`${v.marka} ${v.model}`}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-stone-300">
            <CarFront className="w-12 h-12" strokeWidth={1} />
          </div>
        )}
        <div className="absolute top-3 left-3">
          <DurumBadge durum={v.durum} />
        </div>
        {stokGun !== null && (
          <div className="absolute top-3 right-3 bg-white/90 backdrop-blur px-2 py-1 rounded-full text-[11px] font-semibold text-slate-800 num-tabular">
            {stokGun} gün stokta
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="font-display font-bold text-slate-900 leading-tight">
              {v.marka} {v.model}
            </div>
            <div className="text-xs text-stone-500 mt-0.5">
              {v.yil} · {v.plaka}
            </div>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-3 text-xs text-stone-500">
          <span className="flex items-center gap-1">
            <Gauge className="w-3.5 h-3.5" />
            {numberTR(v.km)} km
          </span>
          <span className="flex items-center gap-1">
            <Fuel className="w-3.5 h-3.5" />
            {v.yakit}
          </span>
          <span className="flex items-center gap-1">
            <Cog className="w-3.5 h-3.5" />
            {v.vites}
          </span>
        </div>
        <div className="mt-4 flex items-end justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-400">
              Maliyet
            </div>
            <div className="font-display font-black text-lg text-slate-900 num-tabular">
              {currencyTRY(maliyet)}
            </div>
          </div>
          {v.durum === "Satıldı" && (
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-stone-400">
                Net Kâr
              </div>
              <div
                className={`font-display font-black text-lg num-tabular ${
                  vehicleProfit(v) >= 0 ? "text-emerald-700" : "text-rose-700"
                }`}
              >
                {currencyTRY(vehicleProfit(v))}
              </div>
            </div>
          )}
          {v.durum === "Stokta" && (
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-stone-400">
                Alış
              </div>
              <div className="font-display font-black text-sm text-stone-700 num-tabular">
                {currencyTRY(v.alisFiyati)}
              </div>
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

function VehicleDetailModal({ v, onClose, onUpdate }) {
  const { user } = useAuth();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [heroIdx, setHeroIdx] = useState(0);
  const gallery = useMemo(() => {
    const list = [];
    if (v.gorsel) list.push({ src: imgSrc(v.gorsel), removable: false });
    for (const p of v.gorseller || []) {
      list.push({ src: storageSrc(p), path: p, removable: true });
    }
    return list;
  }, [v]);
  const hero = gallery[Math.min(heroIdx, gallery.length - 1)];

  const [expForm, setExpForm] = useState({
    kategori: MASRAF_KATEGORI[0],
    aciklama: "",
    tutar: 0,
    tarih: today(),
  });
  const [sellOpen, setSellOpen] = useState(false);
  const [sell, setSell] = useState({
    satisFiyati: "",
    satisTarihi: today(),
    aliciAdi: "",
    aliciTelefon: "",
  });

  const maliyet = vehicleCost(v);

  const onFilesPicked = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);
    const oversize = files.find((f) => f.size > 8 * 1024 * 1024);
    if (oversize) {
      toast.error(`Dosya çok büyük: ${oversize.name} (max 8MB)`);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      await api.post(`/vehicles/${v.id}/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${files.length} fotoğraf yüklendi`);
      onUpdate();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removePhoto = async (path) => {
    if (!window.confirm("Bu fotoğrafı silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/vehicles/${v.id}/photos`, { data: { path } });
      toast.success("Fotoğraf silindi");
      setHeroIdx(0);
      onUpdate();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const addExpense = async () => {
    if (!expForm.tutar) return toast.error("Tutar girin");
    try {
      await api.post(`/vehicles/${v.id}/expenses`, {
        ...expForm,
        tutar: Number(expForm.tutar),
      });
      toast.success("Masraf eklendi");
      setExpForm({
        kategori: MASRAF_KATEGORI[0],
        aciklama: "",
        tutar: 0,
        tarih: today(),
      });
      onUpdate();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const removeExpense = async (id) => {
    try {
      await api.delete(`/vehicles/${v.id}/expenses/${id}`);
      toast.success("Masraf silindi");
      onUpdate();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const removeVehicle = async () => {
    if (!window.confirm("Bu aracı silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/vehicles/${v.id}`);
      toast.success("Araç silindi");
      onClose();
      onUpdate();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const submitSell = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/vehicles/${v.id}/sell`, {
        ...sell,
        satisFiyati: Number(sell.satisFiyati),
      });
      toast.success(`Satış tamamlandı`);
      setSellOpen(false);
      onUpdate();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto p-0 gap-0">
        <div className="aspect-[21/9] bg-stone-100 overflow-hidden relative">
          {hero ? (
            <img
              src={hero.src}
              alt={`${v.marka} ${v.model}`}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-stone-300">
              <CarFront className="w-20 h-20" strokeWidth={1} />
            </div>
          )}
          <div className="absolute bottom-4 left-4">
            <DurumBadge durum={v.durum} />
          </div>
        </div>

        {gallery.length > 0 && (
          <div className="px-6 pt-4 flex gap-2 overflow-x-auto no-scrollbar">
            {gallery.map((g, i) => (
              <div key={i} className="relative shrink-0">
                <button
                  onClick={() => setHeroIdx(i)}
                  data-testid={`veh-thumb-${i}`}
                  className={`w-20 h-14 rounded-lg overflow-hidden border-2 transition-colors ${
                    i === heroIdx
                      ? "border-orange-600"
                      : "border-transparent hover:border-stone-300"
                  }`}
                >
                  <img
                    src={g.src}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </button>
                {g.removable && (
                  <button
                    onClick={() => removePhoto(g.path)}
                    data-testid={`veh-thumb-del-${i}`}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white border border-stone-300 shadow flex items-center justify-center hover:bg-rose-50"
                  >
                    <X className="w-3 h-3 text-rose-700" />
                  </button>
                )}
              </div>
            ))}
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="veh-photo-upload"
              className="shrink-0 w-20 h-14 rounded-lg border-2 border-dashed border-stone-300 text-stone-500 hover:border-orange-600 hover:text-orange-700 flex items-center justify-center disabled:opacity-50"
              title="Fotoğraf ekle"
            >
              <ImagePlus className="w-5 h-5" />
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              className="hidden"
              onChange={(e) => onFilesPicked(e.target.files)}
            />
          </div>
        )}
        {gallery.length === 0 && (
          <div className="px-6 pt-4">
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="veh-photo-upload-empty"
              className="w-full py-3 rounded-xl border-2 border-dashed border-stone-300 text-stone-500 hover:border-orange-600 hover:text-orange-700 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <ImagePlus className="w-5 h-5" />
              <span className="text-sm font-medium">
                {uploading ? "Yükleniyor…" : "Fotoğraf yükle (JPG, PNG, WEBP)"}
              </span>
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              className="hidden"
              onChange={(e) => onFilesPicked(e.target.files)}
            />
          </div>
        )}

        <div className="p-6 md:p-8">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl md:text-3xl text-slate-900">
              {v.marka} {v.model}
            </DialogTitle>
            <DialogDescription className="text-stone-500">
              {v.yil} · {v.plaka} · {v.renk}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
            <div className="rounded-xl bg-stone-50 p-3">
              <div className="text-[10px] uppercase tracking-widest text-stone-500">
                Alış
              </div>
              <div className="font-display font-black text-slate-900 num-tabular">
                {currencyTRY(v.alisFiyati)}
              </div>
            </div>
            <div className="rounded-xl bg-stone-50 p-3">
              <div className="text-[10px] uppercase tracking-widest text-stone-500">
                Toplam Maliyet
              </div>
              <div className="font-display font-black text-slate-900 num-tabular">
                {currencyTRY(maliyet)}
              </div>
            </div>
            <div className="rounded-xl bg-stone-50 p-3">
              <div className="text-[10px] uppercase tracking-widest text-stone-500">
                Km
              </div>
              <div className="font-display font-black text-slate-900 num-tabular">
                {numberTR(v.km)}
              </div>
            </div>
            <div className="rounded-xl bg-stone-50 p-3">
              <div className="text-[10px] uppercase tracking-widest text-stone-500">
                Alış Tarihi
              </div>
              <div className="font-display font-black text-slate-900 text-sm">
                {dateTR(v.alisTarihi)}
              </div>
            </div>
          </div>

          {v.durum === "Satıldı" && (
            <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-emerald-700">
                    Satış
                  </div>
                  <div className="font-display font-black text-xl text-slate-900 num-tabular">
                    {currencyTRY(v.satisFiyati)} · {dateTR(v.satisTarihi)}
                  </div>
                  <div className="text-sm text-stone-600 mt-1">
                    Alıcı: {v.aliciAdi} · {v.aliciTelefon}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-emerald-700">
                    Net Kâr
                  </div>
                  <div
                    className={`font-display font-black text-2xl num-tabular ${
                      vehicleProfit(v) >= 0
                        ? "text-emerald-700"
                        : "text-rose-700"
                    }`}
                  >
                    {currencyTRY(vehicleProfit(v))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {v.notlar && (
            <div className="mt-4 rounded-xl border border-stone-200 p-3 text-sm text-stone-700">
              <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">
                Notlar
              </div>
              {v.notlar}
            </div>
          )}

          {/* Masraflar */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-display font-black text-slate-900">
                Masraflar
              </h4>
              <div className="text-xs text-stone-500 num-tabular">
                Toplam:{" "}
                {currencyTRY(
                  (v.masraflar || []).reduce(
                    (s, m) => s + (m.tutar || 0),
                    0,
                  ),
                )}
              </div>
            </div>
            <div className="space-y-2">
              {(v.masraflar || []).map((m) => (
                <div
                  key={m.id}
                  className="flex items-center gap-3 p-3 rounded-xl border border-stone-200 bg-white"
                >
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-slate-800">
                      {m.kategori}
                    </div>
                    <div className="text-xs text-stone-500">
                      {m.aciklama} · {dateTR(m.tarih)}
                    </div>
                  </div>
                  <div className="font-semibold text-slate-900 num-tabular">
                    {currencyTRY(m.tutar)}
                  </div>
                  <button
                    onClick={() => removeExpense(m.id)}
                    data-testid={`exp-del-${m.id}`}
                    className="p-2 rounded-lg text-stone-400 hover:text-rose-700 hover:bg-rose-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {(v.masraflar || []).length === 0 && (
                <div className="text-sm text-stone-500 p-4 rounded-xl bg-stone-50 text-center">
                  Henüz masraf kaydı yok.
                </div>
              )}
            </div>

            <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-2">
              <select
                data-testid="exp-kategori"
                value={expForm.kategori}
                onChange={(e) =>
                  setExpForm({ ...expForm, kategori: e.target.value })
                }
                className="h-10 px-3 rounded-lg border border-stone-300 text-sm"
              >
                {MASRAF_KATEGORI.map((k) => (
                  <option key={k}>{k}</option>
                ))}
              </select>
              <input
                placeholder="Açıklama"
                data-testid="exp-aciklama"
                value={expForm.aciklama}
                onChange={(e) =>
                  setExpForm({ ...expForm, aciklama: e.target.value })
                }
                className="h-10 px-3 rounded-lg border border-stone-300 text-sm md:col-span-2"
              />
              <input
                type="number"
                placeholder="Tutar (₺)"
                data-testid="exp-tutar"
                value={expForm.tutar}
                onChange={(e) =>
                  setExpForm({ ...expForm, tutar: e.target.value })
                }
                className="h-10 px-3 rounded-lg border border-stone-300 text-sm"
              />
              <div className="flex gap-2">
                <input
                  type="date"
                  data-testid="exp-tarih"
                  value={expForm.tarih}
                  onChange={(e) =>
                    setExpForm({ ...expForm, tarih: e.target.value })
                  }
                  className="h-10 px-2 rounded-lg border border-stone-300 text-sm flex-1"
                />
                <button
                  onClick={addExpense}
                  data-testid="exp-ekle"
                  className="h-10 px-3 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          <DialogFooter className="mt-6 gap-2">
            {user?.role === "admin" && (
              <button
                onClick={removeVehicle}
                data-testid="veh-delete"
                className="h-11 px-4 rounded-full text-rose-700 hover:bg-rose-50 border border-rose-200 font-semibold text-sm flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" /> Aracı sil
              </button>
            )}
            {v.durum !== "Satıldı" && (
              <button
                onClick={() => setSellOpen(true)}
                data-testid="veh-sat"
                className="h-11 px-6 rounded-full bg-orange-700 hover:bg-orange-800 text-white font-semibold text-sm flex items-center gap-2"
              >
                <BadgeDollarSign className="w-4 h-4" /> Sat
              </button>
            )}
          </DialogFooter>
        </div>

        {/* Sell dialog */}
        <Dialog open={sellOpen} onOpenChange={setSellOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display">Aracı Sat</DialogTitle>
              <DialogDescription>
                Satış bilgilerini doldurun. Net kâr otomatik hesaplanacak.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submitSell} className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">
                  Satış Fiyatı (₺)
                </label>
                <input
                  type="number"
                  required
                  data-testid="sell-fiyat"
                  value={sell.satisFiyati}
                  onChange={(e) =>
                    setSell({ ...sell, satisFiyati: e.target.value })
                  }
                  className="mt-1.5 w-full h-11 px-3.5 rounded-xl border border-stone-300"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">
                  Satış Tarihi
                </label>
                <input
                  type="date"
                  required
                  data-testid="sell-tarih"
                  value={sell.satisTarihi}
                  onChange={(e) =>
                    setSell({ ...sell, satisTarihi: e.target.value })
                  }
                  className="mt-1.5 w-full h-11 px-3.5 rounded-xl border border-stone-300"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">
                  Alıcı Adı
                </label>
                <input
                  required
                  data-testid="sell-alici"
                  value={sell.aliciAdi}
                  onChange={(e) =>
                    setSell({ ...sell, aliciAdi: e.target.value })
                  }
                  className="mt-1.5 w-full h-11 px-3.5 rounded-xl border border-stone-300"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">
                  Alıcı Telefon
                </label>
                <input
                  data-testid="sell-tel"
                  value={sell.aliciTelefon}
                  onChange={(e) =>
                    setSell({ ...sell, aliciTelefon: e.target.value })
                  }
                  className="mt-1.5 w-full h-11 px-3.5 rounded-xl border border-stone-300"
                />
              </div>
              {sell.satisFiyati && (
                <div className="rounded-xl bg-stone-50 p-3">
                  <div className="text-xs text-stone-500">Beklenen net kâr</div>
                  <div
                    className={`font-display font-black text-xl num-tabular ${
                      Number(sell.satisFiyati) - maliyet >= 0
                        ? "text-emerald-700"
                        : "text-rose-700"
                    }`}
                  >
                    {currencyTRY(Number(sell.satisFiyati) - maliyet)}
                  </div>
                </div>
              )}
              <DialogFooter>
                <button
                  type="submit"
                  data-testid="sell-submit"
                  className="h-11 px-6 rounded-full bg-orange-700 hover:bg-orange-800 text-white font-semibold text-sm"
                >
                  Satışı Kaydet
                </button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

export default function Inventory() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [durum, setDurum] = useState("Tümü");
  const [marka, setMarka] = useState("Tümü");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    try {
      const res = await api.get("/vehicles");
      setVehicles(res.data);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const markalar = useMemo(
    () => Array.from(new Set(vehicles.map((v) => v.marka))).sort(),
    [vehicles],
  );

  const filtered = useMemo(() => {
    return vehicles.filter((v) => {
      if (durum !== "Tümü" && v.durum !== durum) return false;
      if (marka !== "Tümü" && v.marka !== marka) return false;
      if (q) {
        const s = q.toLowerCase();
        const hay = `${v.marka} ${v.model} ${v.plaka} ${v.renk}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [vehicles, durum, marka, q]);

  return (
    <div data-testid="inventory-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-stone-500">
            Stok
          </div>
          <h1 className="font-display font-black text-3xl md:text-4xl text-slate-900 mt-1">
            Araç Envanteri
          </h1>
        </div>
        <Link
          to="/arac-ekle"
          data-testid="stok-add-btn"
          className="h-11 px-5 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-semibold text-sm flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Yeni Araç
        </Link>
      </div>

      <div className="rounded-2xl border border-stone-200 bg-white p-4 mb-6 flex flex-col md:flex-row gap-3">
        <div className="flex-1 flex items-center gap-2 px-3 h-10 rounded-xl border border-stone-200 bg-stone-50">
          <Search className="w-4 h-4 text-stone-400" />
          <input
            data-testid="stok-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Marka, model, plaka ara…"
            className="flex-1 bg-transparent outline-none text-sm"
          />
        </div>
        <div className="flex gap-2">
          <select
            data-testid="stok-durum"
            value={durum}
            onChange={(e) => setDurum(e.target.value)}
            className="h-10 px-3 rounded-xl border border-stone-200 text-sm bg-white"
          >
            <option>Tümü</option>
            <option>Stokta</option>
            <option>Satıldı</option>
            <option>Rezerve</option>
          </select>
          <select
            data-testid="stok-marka"
            value={marka}
            onChange={(e) => setMarka(e.target.value)}
            className="h-10 px-3 rounded-xl border border-stone-200 text-sm bg-white"
          >
            <option>Tümü</option>
            {markalar.map((m) => (
              <option key={m}>{m}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-72 rounded-2xl" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-stone-300 bg-white/40 p-16 text-center">
          <CarFront className="w-10 h-10 text-stone-300 mx-auto mb-3" />
          <div className="font-display font-bold text-slate-900">
            Sonuç bulunamadı
          </div>
          <div className="text-sm text-stone-500 mt-1">
            Farklı filtre veya arama deneyin.
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((v) => (
            <VehicleCard
              key={v.id}
              v={v}
              onClick={() => setSelected(v)}
            />
          ))}
        </div>
      )}

      {selected && (
        <VehicleDetailModal
          v={vehicles.find((v) => v.id === selected.id) || selected}
          onClose={() => setSelected(null)}
          onUpdate={load}
        />
      )}
    </div>
  );
}
